import {
  Checkbox,
  Group,
  NumberInput,
  Stack,
  Switch,
  Table,
  Text,
  TextInput,
  Textarea,
} from "@mantine/core"
import { useForm } from "@mantine/form"
import { useDisclosure } from "@mantine/hooks"
import { ListBulletsIcon, PencilSimpleIcon, PlusIcon, TrashIcon } from "@phosphor-icons/react"
import { useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { modals } from "@mantine/modals"
import { EmptyStateGuide } from "@dehub/theme"
import { HelpNote } from "../ui"
import { SizeScaleApi } from "../../apis"
import { TableFrame } from "../delivery/atoms"
import { ListState } from "../ui"
import { SETTINGS_WRITE, SettingsActionCell, SettingsButton, SettingsRowAction } from "./settings-controls"
import RenderText from "../ui/render-text"
import type { ISizeScale, ICreateSizeScaleInput } from "../../apis/size-scale"
import { CustomTable, ITableRef } from "../table/customTable"
import { BooleanIndicator } from "./atoms"
import { useSettingsMutations } from "./hooks"
import { SizeDefinitionsModal } from "./SizeDefinitionsModal"
import { SettingsModal } from "./settings-modal"

function SizeScaleFormModal({
  opened,
  onClose,
  initial,
  onSaved,
}: {
  opened: boolean
  onClose: () => void
  initial?: ISizeScale | null
  onSaved?: () => void
}) {
  const { t } = useTranslation("settings")
  const mutations = useSettingsMutations()
  const isEdit = !!initial

  const form = useForm<ICreateSizeScaleInput>({
    initialValues: {
      name: initial?.name ?? "",
      description: initial?.description ?? "",
      unit: initial?.unit ?? "",
      sortOrder: initial?.sortOrder ?? 0,
      isDefault: initial?.isDefault ?? false,
      isActive: initial?.isActive ?? true,
    },
    validate: {
      name: (v) => (!v.trim() ? t("sizeScale.fields.name.label") + " " + t("common.empty") : null),
    },
  })

  const handleSubmit = async (values: ICreateSizeScaleInput) => {
    if (isEdit && initial) {
      await mutations.sizeScale.update(initial.id, values)
    } else {
      await mutations.sizeScale.create(values)
    }
    onSaved?.()
    onClose()
    form.reset()
  }

  return (
    <SettingsModal
      opened={opened}
      onClose={onClose}
      title={isEdit ? t("sizeScale.modal.editTitle") : t("sizeScale.modal.createTitle")}
      onSubmit={form.onSubmit(handleSubmit)}
      submitLabel={isEdit ? t("common.save") : t("common.create")}
      submitting={mutations.submitting}
    >
          <TextInput
            label={t("sizeScale.fields.name.label")}
            description={t("sizeScale.fields.name.description")}
            required
            {...form.getInputProps("name")}
          />
          <Textarea
            label={t("holiday.holidays.fields.description.label")}
            autosize
            minRows={2}
            {...form.getInputProps("description")}
          />
          <TextInput
            label={t("sizeScale.fields.unit.label")}
            description={t("sizeScale.fields.unit.description")}
            placeholder={t("sizeScale.fields.unit.placeholder")}
            {...form.getInputProps("unit")}
          />
          <NumberInput
            label={t("sizeScale.fields.sortOrder.label")}
            description={t("sizeScale.fields.sortOrder.description")}
            min={0}
            {...form.getInputProps("sortOrder")}
          />
          <Checkbox
            label={t("sizeScale.fields.isDefault.label")}
            description={t("sizeScale.fields.isDefault.description")}
            checked={form.values.isDefault}
            onChange={(e) => form.setFieldValue("isDefault", e.currentTarget.checked)}
          />
          <Checkbox
            label={t("sizeScale.fields.isActive.label")}
            checked={form.values.isActive}
            onChange={(e) => form.setFieldValue("isActive", e.currentTarget.checked)}
          />
    </SettingsModal>
  )
}

export function SizeScaleTab() {
  const { t } = useTranslation("settings")
  const [search, setSearch] = useState("")
  const [activeOnly, setActiveOnly] = useState(false)
  const [editTarget, setEditTarget] = useState<ISizeScale | null>(null)
  const [createOpen, { open: openCreate, close: closeCreate }] = useDisclosure(false)
  const [editOpen, { open: openEdit, close: closeEdit }] = useDisclosure(false)
  const [definitionsTarget, setDefinitionsTarget] = useState<ISizeScale | null>(null)
  const [definitionsOpen, { open: openDefinitions, close: closeDefinitions }] = useDisclosure(false)
  const tableRef = useRef<ITableRef>(null)
  const mutations = useSettingsMutations()

  const filters = useMemo(() => {
    const next: { search?: string; isActive?: boolean } = {}
    if (search) next.search = search
    if (activeOnly) next.isActive = true
    return next
  }, [search, activeOnly])

  const handleEdit = (scale: ISizeScale) => {
    setEditTarget(scale)
    openEdit()
  }

  const handleDelete = (scale: ISizeScale) => {
    modals.openConfirmModal({
      title: t("common.delete"),
      children: (
        <>
          <Text size="md">
            {t("common.confirmDelete", { name: scale.name })}
          </Text>
          <HelpNote helpKey="order.settings.confirm.delete-size" />
        </>
      ),
      labels: { confirm: t("common.delete"), cancel: t("common.cancel") },
      confirmProps: { color: "red" },
      onConfirm: async () => {
        await mutations.sizeScale.remove(scale.id)
        tableRef.current?.reload()
      },
    })
  }

  return (
    <Stack gap="md">
      <Group justify="space-between" align="flex-start">
        <Stack gap={2}>
          <Text fw={650} fz="lg">{t("sizeScale.title")}</Text>
          <Text fz="sm" c="gray.8">{t("sizeScale.description")}</Text>
        </Stack>
        <SettingsButton code={SETTINGS_WRITE.size} helpKey="order.settings.action.new-size" helpId="settings-new-size" leftSection={<PlusIcon size={18} />} onClick={openCreate}>
          {t("sizeScale.actions.newSize")}
        </SettingsButton>
      </Group>

      <Group gap="sm">
          <TextInput
            placeholder={t("common.empty")}
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
            w={220}
          />
          <Switch
            label={t("sizeScale.fields.isActive.label")}
            checked={activeOnly}
            onChange={(e) => setActiveOnly(e.currentTarget.checked)}
          />
      </Group>

      <TableFrame>
      <Table.ScrollContainer minWidth={700}>
        <CustomTable
          ref={tableRef}
          name="size-scales"
          colCount={6}
          filters={filters}
          empty={
            <EmptyStateGuide
              helpKey="order.settings.empty.main"
              when={search || activeOnly ? "noResults" : "size-scale"}
              actions={{ create: openCreate }}
              fallback={<ListState state={search || activeOnly ? "noResults" : "empty"} title={t("common.empty")} description={search || activeOnly ? undefined : t("common.emptyHint")} action={search || activeOnly ? { onClick: () => { setSearch(""); setActiveOnly(false) } } : undefined} />}
            />
          }
          renderError={(retry) => <ListState state="error" title={t("common.error")} description={t("common.loadError")} onRetry={retry} />}
          loadData={async (filter) => {
            const page = filter?.offset?.page ?? 1
            const limit = filter?.offset?.limit ?? 20
            const f = (filter?.filter ?? {}) as { search?: string; isActive?: boolean }
            return SizeScaleApi.list({
              filter: {
                ...(f.search ? { search: f.search } : {}),
                ...(f.isActive !== undefined ? { isActive: f.isActive } : {}),
              },
              offset: { limit, skip: (page - 1) * limit },
            })
          }}
          thead={
            <Table.Thead bg="var(--nw-table-head-bg)">
              <Table.Tr>
                <Table.Th w={160}>{t("common.actions")}</Table.Th>
                <Table.Th>{t("sizeScale.fields.name.label")}</Table.Th>
                <Table.Th>{t("sizeScale.fields.unit.label")}</Table.Th>
                <Table.Th>{t("sizeScale.fields.sortOrder.label")}</Table.Th>
                <Table.Th>{t("sizeScale.fields.isDefault.label")}</Table.Th>
                <Table.Th>{t("sizeScale.fields.isActive.label")}</Table.Th>
              </Table.Tr>
            </Table.Thead>
          }
          tbody={(rows: ISizeScale[]) => (
            <Table.Tbody>
              {rows.map((scale) => (
                <Table.Tr key={scale.id}>
                  <Table.Td>
                    <SettingsActionCell>
                      <SettingsRowAction
                        code={SETTINGS_WRITE.size}
                        icon={<ListBulletsIcon />}
                        label={t("sizeScale.actions.editDefinitions")}
                        onClick={() => {
                          setDefinitionsTarget(scale)
                          openDefinitions()
                        }}
                      />
                      <SettingsRowAction
                        code={SETTINGS_WRITE.size}
                        icon={<PencilSimpleIcon />}
                        label={t("common.edit")}
                        onClick={() => handleEdit(scale)}
                      />
                      <SettingsRowAction
                        code={SETTINGS_WRITE.size}
                        icon={<TrashIcon />}
                        label={t("common.delete")}
                        onClick={() => handleDelete(scale)}
                      />
                    </SettingsActionCell>
                  </Table.Td>
                  <Table.Td>
                    <RenderText text={scale.name} fontWeight={600} />
                  </Table.Td>
                  <Table.Td>
                    <RenderText text={scale.unit || "-"} />
                  </Table.Td>
                  <Table.Td>
                    <RenderText text={String(scale.sortOrder)} />
                  </Table.Td>
                  <Table.Td>
                    <BooleanIndicator value={scale.isDefault} trueLabel={t("sizeScale.fields.isDefault.label")} />
                  </Table.Td>
                  <Table.Td>
                    <BooleanIndicator value={scale.isActive} trueLabel={t("common.active")} falseLabel={t("common.inactive")} />
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          )}
        />
      </Table.ScrollContainer>
      </TableFrame>

      <SizeScaleFormModal
        opened={createOpen}
        onClose={closeCreate}
        onSaved={() => tableRef.current?.reload()}
      />
      <SizeScaleFormModal
        opened={editOpen}
        onClose={closeEdit}
        initial={editTarget}
        onSaved={() => tableRef.current?.reload()}
      />
      <SizeDefinitionsModal
        opened={definitionsOpen}
        onClose={closeDefinitions}
        scale={definitionsTarget}
      />
    </Stack>
  )
}
